import React from 'react';
import { fixedNumberFromInput } from '../../../../utils/utils';

interface AnimationRangeInputProps {
	value: number;
	onChange: (value: number) => void;
	min: number;
	max: number;
	step: number;
	className?: string;
	snap?: boolean; //if snap, component will only allow the "tick" values
	ticks: Array<{
		label: string;
		position?: 'left' | 'center' | 'right';
		isInfinity?: boolean;
	}>;
}

export default function AnimationRangeInput({
	value,
	onChange,
	min,
	max,
	step,
	ticks,
	snap = false,
	className = '',
}: AnimationRangeInputProps) {
	
	const getSnapValues = () => {
		const snapValues = [min]; // Always include the minimum value
		
		// Map tick labels to actual values
		for (const tick of ticks) {
			if (tick.isInfinity) {
				snapValues.push(max);
			} else {
				// Extract numeric value from label (e.g., "1s" -> 1, "5s" -> 5)
				const numericValue = parseFloat(tick.label.replace(/[^\d.]/g, ''));
				if (!isNaN(numericValue) && numericValue >= min && numericValue <= max) {
					snapValues.push(numericValue);
				}
			}
		}
		
		// Sort and remove duplicates
		return [...new Set(snapValues)].sort((a, b) => a - b);
	};

	const snapValues = getSnapValues();
	
	// When snapping, we use the index in the snapValues array as the range value
	// and convert back to the actual value
	const getRangeProps = () => {
		if (snap) {
			const currentIndex = snapValues.findIndex(val => val === value);
			const safeIndex = currentIndex >= 0 ? currentIndex : 0;
			
			return {
				min: 0,
				max: snapValues.length - 1,
				step: 1,
				value: safeIndex,
				percentage: (safeIndex / (snapValues.length - 1)) * 100
			};
		} else {
			return {
				min,
				max,
				step,
				value,
				percentage: ((value - min) / (max - min)) * 100
			};
		}
	};

	const rangeProps = getRangeProps();

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const inputValue = fixedNumberFromInput(e.target.value);
		
		if (snap) {
			// Convert index back to actual value
			const actualValue = snapValues[inputValue] || snapValues[0];
			onChange(actualValue);
		} else {
			onChange(inputValue);
		}
	};

	return (
		<div className={`animation-range-container ${className}`}>
			<div className="animation-range-wrapper">
				<div 
					className="animation-range-track-filled"
					style={{width: `${rangeProps.percentage}%`}}
				/>
				<div className="animation-range-track-empty" />
				<div 
					className="animation-range-thumb"
					style={{left: `${rangeProps.percentage}%`}}
				/>
				<input
					className="animation-range-input"
					onChange={handleChange}
					value={rangeProps.value}
					type="range"
					step={rangeProps.step}
					min={rangeProps.min}
					max={rangeProps.max}
				/>
			</div>
			<div className="animation-range-ticks">
				{ticks.map((tick, index) => (
					<div 
						key={index}
						className={`animation-range-tick bl-font-p2 ${
							tick.position === 'left' ? 'animation-range-tick--left' : 
							tick.position === 'center' ? 'animation-range-tick--center' : 
							tick.position === 'right' ? 'animation-range-tick--right' : ''
						} ${tick.isInfinity ? 'animation-range-tick--infinity' : ''}`}
					>
						{tick.label}
					</div>
				))}
			</div>
		</div>
	);
}